import React, { useState, useEffect } from "react";
import { Snackbar, IconButton, Alert, TextField, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OfficeLocationFilter, ProgramFilter, ReportFilterBar } from "../ReportFilters";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledDataGrid from '../../DataGrid/ControlledDataGrid';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

export default function DailyAttendanceSummary(props) {
    const [gridId, setGridId] = useState(69);
    const apiRef = useGridApiRef();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [sessionDate, setSessionDate] = useState(moment().format("YYYY-MM-DD"));
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [reportDetails, setReportDetails] = useState({});
    const [isFavorite, setIsFavorite] = useState(false);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        axios
            .get(APIURL + "/jsreport/getAllReports", {
                params: { empID: props.user.emp_id },
            })
            .then((response) => {
                const report = response?.data?.reports.find((ele) => ele.report_name === "Daily Attendance Report");
                setReportDetails(report);
                setIsFavorite(report ? (report.status === 1 ? true : false) : false);
            });

        axios
            .get(APIURL + "/reports/getSessionsByDate", {
                params: {
                    compID: props.user.comp_id,
                    sessionDate: sessionDate,
                    oloc_id: selectedOffice || undefined,
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data.length !== 0 ? response?.data.filter((session) => session.tas_id) : []);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
            });
    };

    const rows = data.map((data, idx) => {
        return {
            hierarchy: [data.cus_name, `(${idx}) - ` + data.tas_id],
            id: idx,
            cus_id: data.cus_id,
            tas_id: data.tas_id,
            task_name: data.tas_title,
            task_type: data.tas_type,
            tas_time_spent: Number(data.taskHrs || 0).toFixed(2),
            units_spent: data.unitsSpent,
            auth_units: data.authUnits ? data.authUnits : '-',
            remaining_units: data.authUnits && data.unitsSpent !== '-' ? (data.authUnits - Number(data.unitsSpent || 0)) : '-',
        };
    });

    const columns = [
        { field: "cus_id", headerName: "Customer ID" },
        { field: "tas_id", headerName: "Task ID" },
        { field: "task_name", headerName: "Session Name", width: 150 },
        { field: "task_type", headerName: "Session Task Type", width: 150 },
        { field: "tas_time_spent", headerName: "Session Duration", width: 150 },
        { field: "units_spent", headerName: "Units Spent", width: 150 },
        { field: "auth_units", headerName: "Authorized Units", width: 150 },
        { field: "remaining_units", headerName: "Units Remaining", width: 150 },
    ];

    const markFavourite = async (data, type) => {
        const payload = {
            report_id: data?.report_id,
            status: type,
            empID: props.user.emp_id
        };

        axios
            .put(APIURL + "/jsreport/markFavorite", payload)
            .then((response) => {
                if (response?.status === 200 && response?.data?.code === 200) {
                    setSuccessText(response?.data?.status_message || "Favorite Modifed Succesfully");
                } else {
                    setErrorText("Something went wrong");
                }

                fetchData();
            });
    }

    return (
        <div style={{ width: '100%', padding: 10 }}>
            <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between' }}>
                <ReportFilterBar onSearch={() => fetchData()} style={{ flexWrap: 'wrap', rowGap: 10 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            id="date"
                            label="Session Date"
                            value={new Date(moment(sessionDate).format('YYYY-MM-DDTHH:mm:ss'))}
                            onChange={(d) => {
                                setSessionDate(moment(d).format('YYYY-MM-DD'));
                            }}
                            slotProps={{ textField: { variant: 'outlined' }, textField: { size: 'small' } }}
                        />
                    </LocalizationProvider>

                    <OfficeLocationFilter
                        comp_id={props.user.comp_id}
                        value={selectedOffice}
                        onChange={setSelectedOffice}
                    />

                    <ProgramFilter
                        comp_id={props.user.comp_id}
                        value={selectedProgram}
                        onChange={setSelectedProgram}
                    />
                </ReportFilterBar>
                <div>
                    <Tooltip title={isFavorite ? "Remove from favorite reports" : "Add to favorite reports"}>
                        <img
                            src={isFavorite ? "./star-solid.svg" : "./star-regular.svg"}
                            alt="barchart"
                            style={{
                                fontFamily: "Lato",
                                width: "1.5rem",
                                height: "1.5rem",
                                cursor: "pointer",
                                margin: 5
                            }}
                            onClick={() => markFavourite(reportDetails, isFavorite ? 0 : 1)}
                        />
                    </Tooltip>
                </div>
            </div>
            <div style={{ height: 'calc(100vh - 250px)' }}>
                <ControlledDataGrid
                    gridId={gridId}
                    user={props.user}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    groupingColDef={{
                        headerName: "Client",
                        width: 300,
                    }}
                    treeData
                    getTreeDataPath={(row) => row.hierarchy}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                />
            </div>
            <Snackbar
                open={successText !== ""}
                autoHideDuration={30000}
                onClose={() => setSuccessText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"success"}
                    elevation={6}
                    variant="filled"
                >
                    {successText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setSuccessText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
            <Snackbar
                open={errorText !== ""}
                autoHideDuration={30000}
                onClose={() => setErrorText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity={"error"}
                    elevation={6}
                    variant="filled"
                >
                    {errorText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setErrorText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
        </div>
    );
}